import React, { useEffect, useMemo, useState } from 'react';
import EcxDatePicker, { EcxDateRangePicker } from '../../../common/EcxDatePicker';
import {
    ACTIVITY_MEDIUM_TYPES,
    ACTIVITY_STATUSES,
    DEFAULT_ACTIVITY_FILTERS,
    getActiveFilterChips,
} from './activityQuery';

function FilterField({ label, icon, children }) {
    return (
        <div className="activity-filter-field">
            <label className="activity-filter-field__label">
                {icon && <i className={`bi ${icon}`}></i>}
                {label}
            </label>
            {children}
        </div>
    );
}

function ActivityFilters({
    filters,
    appliedFilters,
    onFiltersChange,
    onApply,
    onQuickFilter,
    onClear,
    onRemoveChip,
    loading = false,
}) {
    const [expanded, setExpanded] = useState(false);
    const [dateMode, setDateMode] = useState(
        appliedFilters.dateFrom || appliedFilters.dateTo ? 'range' : 'single'
    );

    useEffect(() => {
        if (appliedFilters.dateFrom || appliedFilters.dateTo) {
            setDateMode('range');
        } else if (appliedFilters.date) {
            setDateMode('single');
        }
    }, [appliedFilters.date, appliedFilters.dateFrom, appliedFilters.dateTo]);

    const activeChips = useMemo(
        () => getActiveFilterChips(appliedFilters),
        [appliedFilters]
    );

    const pendingChanges = useMemo(
        () => JSON.stringify(filters) !== JSON.stringify(appliedFilters),
        [filters, appliedFilters]
    );

    const handleQuickFilter = (field, value) => {
        onQuickFilter(field, value);
    };

    const updateFilter = (field, value) => {
        onFiltersChange((prev) => {
            const next = { ...prev, [field]: value };

            if (field === 'date' && value) {
                next.dateFrom = '';
                next.dateTo = '';
            }

            if ((field === 'dateFrom' || field === 'dateTo') && value) {
                next.date = '';
            }

            if (field === 'category') {
                return next;
            }

            return next;
        });
    };

    const handleDateModeChange = (mode) => {
        setDateMode(mode);
        onFiltersChange((prev) => ({
            ...prev,
            date: mode === 'single' ? prev.date : '',
            dateFrom: mode === 'range' ? prev.dateFrom : '',
            dateTo: mode === 'range' ? prev.dateTo : '',
        }));
    };

    const handleApply = () => {
        onApply(filters);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleApply();
        }
    };

    return (
        <div className="activity-filter-panel">
            <div className="activity-filter-panel__top">
                <div className="activity-filter-panel__search input-group input-group-sm">
                    <span className="input-group-text bg-white border-end-0">
                        <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                        type="text"
                        className="form-control form-control-sm border-start-0"
                        placeholder="Search patient name..."
                        value={filters.patientName}
                        onChange={(e) => updateFilter('patientName', e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={loading}
                    />
                    <button
                        type="button"
                        className="btn btn-sm btn-accent"
                        onClick={handleApply}
                        disabled={loading}
                    >
                        Search
                    </button>
                </div>

                <div className="activity-filter-panel__quick-actions">
                    <button
                        type="button"
                        className={`btn btn-sm ${expanded ? 'btn-accent' : 'btn-outline-secondary'} activity-filter-panel__toggle`}
                        onClick={() => setExpanded((prev) => !prev)}
                        disabled={loading}
                    >
                        <i className="bi bi-sliders"></i>
                        <span>Filters</span>
                        {activeChips.length > 0 && (
                            <span className="activity-filter-panel__count">{activeChips.length}</span>
                        )}
                    </button>

                    {(activeChips.length > 0 || pendingChanges) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={onClear}
                            disabled={loading}
                        >
                            Clear all
                        </button>
                    )}
                </div>
            </div>

            <div className="activity-filter-panel__compact">
                <div className="activity-filter-panel__group">
                    <span className="activity-filter-panel__group-label">Medium</span>
                    <div className="activity-filter-pills">
                        {ACTIVITY_MEDIUM_TYPES.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className={`activity-filter-pill ${
                                    filters.mediumType === option.value ? 'is-active' : ''
                                }`}
                                onClick={() => handleQuickFilter('mediumType', option.value)}
                                disabled={loading}
                            >
                                <i className={`bi ${option.icon}`}></i>
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="activity-filter-panel__group activity-filter-panel__group--status">
                    <span className="activity-filter-panel__group-label">Status</span>
                    <div className="activity-filter-pills activity-filter-pills--wrap">
                        {ACTIVITY_STATUSES.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className={`activity-filter-pill ${
                                    filters.status === option.value ? 'is-active' : ''
                                }`}
                                onClick={() => handleQuickFilter('status', option.value)}
                                disabled={loading}
                            >
                                <i className={`bi ${option.icon}`}></i>
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="activity-filter-panel__group activity-filter-panel__group--date">
                    <span className="activity-filter-panel__group-label">Date</span>
                    <EcxDatePicker
                        className="activity-filter-panel__date-picker"
                        value={filters.date}
                        onChange={(value) => {
                            setDateMode('single');
                            handleQuickFilter('date', value);
                        }}
                        placeholder="Any date"
                        disabled={loading || dateMode === 'range'}
                        size="sm"
                    />
                </div>
            </div>

            {expanded && (
                <div className="activity-filter-panel__expanded">
                    <div className="activity-filter-panel__section">
                        <div className="activity-filter-panel__section-title">
                            <i className="bi bi-person"></i>
                            Patient details
                        </div>
                        <div className="activity-filter-panel__grid">
                            <FilterField label="First name" icon="bi-person-badge">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Starts with..."
                                    value={filters.firstName}
                                    onChange={(e) => updateFilter('firstName', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={loading}
                                />
                            </FilterField>
                            <FilterField label="Last name" icon="bi-person-vcard">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Starts with..."
                                    value={filters.lastName}
                                    onChange={(e) => updateFilter('lastName', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={loading}
                                />
                            </FilterField>
                            <FilterField label="Preferred name" icon="bi-heart">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Starts with..."
                                    value={filters.preferredName}
                                    onChange={(e) => updateFilter('preferredName', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={loading}
                                />
                            </FilterField>
                        </div>
                    </div>

                    <div className="activity-filter-panel__section">
                        <div className="activity-filter-panel__section-title">
                            <i className="bi bi-activity"></i>
                            Activity details
                        </div>
                        <div className="activity-filter-panel__grid">
                            <FilterField label="Activity name" icon="bi-card-text">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search activity name..."
                                    value={filters.activityName}
                                    onChange={(e) => updateFilter('activityName', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={loading}
                                />
                            </FilterField>
                            <FilterField label="Receiver" icon="bi-send">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Email or phone..."
                                    value={filters.receiver}
                                    onChange={(e) => updateFilter('receiver', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={loading}
                                />
                            </FilterField>
                        </div>
                    </div>

                    <div className="activity-filter-panel__section">
                        <div className="activity-filter-panel__section-title">
                            <i className="bi bi-calendar3"></i>
                            Date range
                        </div>
                        <div className="activity-filter-date-mode">
                            <button
                                type="button"
                                className={`activity-filter-date-mode__btn ${
                                    dateMode === 'single' ? 'is-active' : ''
                                }`}
                                onClick={() => handleDateModeChange('single')}
                                disabled={loading}
                            >
                                Single day
                            </button>
                            <button
                                type="button"
                                className={`activity-filter-date-mode__btn ${
                                    dateMode === 'range' ? 'is-active' : ''
                                }`}
                                onClick={() => handleDateModeChange('range')}
                                disabled={loading}
                            >
                                Date range
                            </button>
                        </div>
                        {dateMode === 'range' ? (
                            <FilterField label="Date range" icon="bi-calendar-range">
                                <EcxDateRangePicker
                                    startValue={filters.dateFrom}
                                    endValue={filters.dateTo}
                                    onStartChange={(value) => updateFilter('dateFrom', value)}
                                    onEndChange={(value) => updateFilter('dateTo', value)}
                                    disabled={loading}
                                    size="sm"
                                />
                            </FilterField>
                        ) : (
                            <FilterField label="On date" icon="bi-calendar-day">
                                <EcxDatePicker
                                    value={filters.date}
                                    onChange={(value) => updateFilter('date', value)}
                                    placeholder="Select a date"
                                    disabled={loading}
                                    size="sm"
                                />
                            </FilterField>
                        )}
                    </div>

                    <div className="activity-filter-panel__footer">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                                onFiltersChange(DEFAULT_ACTIVITY_FILTERS);
                                setDateMode('single');
                            }}
                            disabled={loading}
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-accent"
                            onClick={handleApply}
                            disabled={loading}
                        >
                            <i className="bi bi-funnel me-1"></i>
                            Apply filters
                        </button>
                    </div>
                </div>
            )}

            {activeChips.length > 0 && (
                <div className="activity-filter-panel__chips">
                    <span className="activity-filter-panel__chips-label">Active</span>
                    <div className="activity-filter-chips">
                        {activeChips.map((chip) => (
                            <button
                                key={`${chip.field}-${chip.label}`}
                                type="button"
                                className="activity-filter-chip"
                                onClick={() => onRemoveChip(chip.field, chip.clearValue)}
                                disabled={loading}
                                title="Remove filter"
                            >
                                {chip.label}
                                <i className="bi bi-x-lg"></i>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ActivityFilters;
